import { useEffect, useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import { ArrowUpDown, Loader } from 'lucide-react';
import { Helmet } from 'react-helmet-async';
import api from '../api/api';
import LatestTours from '../components/LatestTours';
import SearchBar from '../components/SearchBar';
import CategoryFilter from '../components/CategoryFilter';

export default function Tours() {
  const [tours, setTours] = useState([]);
  const [loading, setLoading] = useState(true);
  const [pagination, setPagination] = useState({});
  const [searchParams] = useSearchParams();

  const search = searchParams.get('search') || '';
  const category = searchParams.get('category') || '';
  const sort = searchParams.get('sort') || 'latest';
  const page = searchParams.get('page') || 1;

  useEffect(() => {
    const fetchTours = async () => {
      setLoading(true);
      try {
        const params = {
          page,
          limit: 12,
        };

        if (search) params.search = search;
        if (category) params.category = category;
        if (sort) params.sort = sort;

        const res = await api.get('/tours', { params });
        setTours(res.data.tours);
        setPagination(res.data.pagination);
      } catch (error) {
        console.error('Error fetching tours:', error);
      } finally {
        setLoading(false);
      }
    };

    fetchTours();
  }, [search, category, sort, page]);

  const handleSortChange = (e) => {
    const params = new URLSearchParams(searchParams);
    params.set('sort', e.target.value);
    params.set('page', 1);
    window.history.pushState({}, '', `?${params.toString()}`);
  };

  const handlePageChange = (pageNum) => {
    const params = new URLSearchParams(searchParams);
    params.set('page', pageNum);
    window.history.pushState({}, '', `?${params.toString()}`);
    window.scrollTo(0, 0);
  };

  return (
    <div className="bg-white dark:bg-slate-950 min-h-screen py-12">
      <Helmet>
  <title>Tours — YS Sky Travel and Tour Agency P.L.C</title>
  <meta name="description" content="Browse our curated tours across Ethiopia and international destinations." />
</Helmet>
      <div className="container">
        {/* Header */}
        <div className="mb-12">
          <h1 className="section-title text-5xl md:text-6xl mb-4">
            Explore Our <span className="text-green-500">Tours</span>
          </h1>
          <p className="section-subtitle text-xl">
            Find and book the perfect tour for your next adventure
          </p>
        </div>

        {/* Filter Section */}
        <div className="bg-gray-50 dark:bg-slate-900 rounded-2xl p-6 md:p-8 mb-12 border border-gray-200 dark:border-slate-800">
          <div className="space-y-6">
            <div>
              <h3 className="text-lg font-semibold text-gray-900 dark:text-white mb-4">Search Tours</h3>
              <SearchBar />
            </div>

            <div className="pt-6 border-t border-gray-200 dark:border-slate-800">
              <h3 className="text-lg font-semibold text-gray-900 dark:text-white mb-4">Filter by Category</h3>
              <CategoryFilter />
            </div>

            <div className="pt-6 border-t border-gray-200 dark:border-slate-800">
              <div className="flex items-center gap-3">
                <ArrowUpDown className="w-5 h-5 text-green-500" />
                <label htmlFor="sort" className="text-sm font-medium text-gray-700 dark:text-gray-300">
                  Sort by:
                </label>
                <select
                  id="sort"
                  value={sort}
                  onChange={handleSortChange}
                  className="px-4 py-2 rounded-lg border border-gray-200 dark:border-slate-700 bg-white dark:bg-slate-800 text-gray-900 dark:text-white focus:outline-none focus:ring-2 focus:ring-green-500 dark:focus:ring-green-400"
                >
                  <option value="latest">Latest</option>
                  <option value="price-low">Price: Low to High</option>
                  <option value="price-high">Price: High to Low</option>
                  <option value="rating">Top Rated</option>
                </select>
              </div>
            </div>
          </div>
        </div>

        {/* Results */}
        
          <>
            <div className="mb-8">
              <p className="text-sm text-gray-600 dark:text-gray-400">
                Showing <span className="font-semibold">{tours.length}</span> of <span className="font-semibold">{pagination.total}</span> tours
              </p>
            </div>

            <LatestTours tours={tours} />

            {/* Pagination */}
            {pagination.pages > 1 && (
              <div className="flex justify-center items-center gap-2 mt-16">
                {[...Array(pagination.pages)].map((_, i) => (
                  <button
                    key={i + 1}
                    onClick={() => handlePageChange(i + 1)}
                    className={`px-4 py-2 rounded-lg font-medium transition-all duration-200 ${
                      parseInt(page) === i + 1
                        ? 'bg-green-500 text-white shadow-lg'
                        : 'bg-gray-100 dark:bg-slate-800 text-gray-900 dark:text-white hover:bg-gray-200 dark:hover:bg-slate-700'
                    }`}
                  >
                    {i + 1}
                  </button>
                ))}
              </div>
            )}
          </>
       
      </div>
    </div>
  );
}
